<template>
  <div class="employee-fair-pay-card-header">
    <div class="employee-fair-pay-card-header__page-header">
      <div class="employee-fair-pay-card-header__top-row">
        <ExBreadcrumbs
          class="employee-fair-pay-card-header__breadcrumbs"
          :breadcrumbsList="breadcrumbs"
          @onClickBreadcrumb="handleBreadcrumbClick"
        />
        <EmployeeAssignmentsBadge
          class="employee-fair-pay-card-header__assignments-badge"
          :primary-position-name="primaryPositionName"
          :preparing-for-position-name="preparingForPositionName"
        />
      </div>

      <div class="employee-fair-pay-card-header__main-row">
        <div class="employee-fair-pay-card-header__identity">
          <ExAvatar
            :picture="avatar"
            :firstName="firstName"
            :lastName="lastName"
            size="large"
            class="employee-fair-pay-card-header__avatar"
          />
          <div class="employee-fair-pay-card-header__info">
            <div class="employee-fair-pay-card-header__name-row">
              <span class="employee-fair-pay-card-header__name">{{ name }}</span>
              <span v-if="title" class="employee-fair-pay-card-header__title">{{ title }}</span>
            </div>
            <div class="employee-fair-pay-card-header__badge-row">
              <OrgObjectStatusBadge
                v-if="status"
                :status="status"
                :archived="archived"
              />
              <span v-if="!tracking" class="employee-fair-pay-card-header__not-tracking">
                Not Tracking
              </span>
            </div>
          </div>
        </div>

        <div class="employee-fair-pay-card-header__readiness">
          <EmployeeReadinessHeaderBox
            :readiness="readiness"
            :last_month_readiness="last_month_readiness"
            :readiness-title="readinessTitle"
            :show-tooltip="showReadinessTooltip"
            :tooltip-title="readinessTooltipTitle"
          />
        </div>

        <div v-if="showActions" class="employee-fair-pay-card-header__actions">
          <button
            type="button"
            class="employee-fair-pay-card-header__action-button"
            aria-label="Open chatter"
            @click="openChatter"
          >
            <img
              :src="chatterIcon"
              class="employee-fair-pay-card-header__chatter-icon"
              alt=""
            />
          </button>
          <EmployeeHorizontalSideBar
            v-if="showMenu"
            class="employee-fair-pay-card-header__overflow-menu"
            @employeeUpdated="handleEmployeeUpdate"
            @employeeStatusChanged="handleEmployeeStatusChanged"
            @employeeDeleted="handleEmployeeDeleted"
          />
        </div>
      </div>
    </div>

    <EmployeeFairPayMenu
      :items="menuItems"
      :value="selectedTab"
      @onChange="handleTabChange"
    />

    <div class="employee-fair-pay-card-header__tab-content">
      <slot />
    </div>

    <ChecklistDrawer
      :isEmployeeChatter="true"
      :drawer="chatterDrawer.show"
      :avatar="{
        picture: avatar,
        firstName: firstName,
        lastName: lastName,
      }"
      :typeValue="name"
      :type="'Employee'"
      object-type="employee"
      :object-id="chatterObjectId"
      :chatterList="chatterDrawer.items"
      :totalCheckpointChatter="chatterDrawer.total"
      @closeDrawer="closeChatterDrawer"
      @requestMoreCheckpointsChatter="loadMoreChatter"
    />
  </div>
</template>

<script setup>
import { computed, ref, getCurrentInstance, onMounted } from "vue";

import ExAvatar from "@components/common/ExAvatar.vue";
import ExBreadcrumbs from "@components/breadcrumbs/ExBreadcrumbs.vue";
import EmployeeAssignmentsBadge from "@components/EmployeeAssignmentsBadge/EmployeeAssignmentsBadge.vue";
import EmployeeHorizontalSideBar from "@components/common/EmployeeHorizontalSideBar.vue";
import EmployeeReadinessHeaderBox from "@components/EmployeeReadinessHeaderBox/EmployeeReadinessHeaderBox.vue";
import EmployeeFairPayMenu from "@components/EmployeeFairPayMenu/EmployeeFairPayMenu.vue";
import OrgObjectStatusBadge from "@components/organizations/statusBadge/OrgObjectStatusBadge.vue";
import ChecklistDrawer from "@components/Drawer/ChatterDrawer.vue";
import chatterIcon from "@components/assets/icons/chatter.svg";
import { useEmployeeStore } from "@/store/pinia/employee";
import {
  EMPLOYEE_FAIR_PAY_MENU_ITEMS,
  EMPLOYEE_FAIR_PAY_TAB,
} from "@components/EmployeeFairPayMenu/employeeFairPayMenuConstants";

const props = defineProps({
  name: {
    type: String,
    default: "",
  },
  title: {
    type: String,
    default: "",
  },
  avatar: {
    type: String,
    default: "",
  },
  status: {
    type: String,
    default: "active",
  },
  archived: {
    type: Boolean,
    default: false,
  },
  tracking: {
    type: Boolean,
    default: true,
  },
  readiness: {
    type: Number,
    default: 0,
  },
  last_month_readiness: {
    type: [Number, null],
    default: null,
  },
  readinessTitle: {
    type: String,
    default: "Readiness",
  },
  showReadinessTooltip: {
    type: Boolean,
    default: false,
  },
  readinessTooltipTitle: {
    type: String,
    default: "",
  },
  breadcrumbs: {
    type: Array,
    default: () => [
      { text: "Pay Plans", href: "#" },
      { text: "Employee", disabled: true },
    ],
  },
  primaryPositionName: {
    type: String,
    default: "",
  },
  preparingForPositionName: {
    type: String,
    default: "",
  },
  menuItems: {
    type: Array,
    default: () => EMPLOYEE_FAIR_PAY_MENU_ITEMS,
  },
  selectedTab: {
    type: Number,
    default: EMPLOYEE_FAIR_PAY_TAB.PAY_PLANS,
  },
  showActions: {
    type: Boolean,
    default: true,
  },
  showMenu: {
    type: Boolean,
    default: true,
  },
  employeeId: {
    type: [String, Number],
    default: null,
  },
  employeeData: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits([
  "breadcrumb-click",
  "onChange",
  "input",
  "employeeUpdated",
  "employeeStatusChanged",
  "employeeDeleted",
]);

const instance = getCurrentInstance();
const { proxy } = instance;
const employeeStore = useEmployeeStore();

const chatterDrawer = ref({
  show: false,
  items: [],
  total: 0,
  page: 1,
  loading: false,
});

const firstName = computed(() => props.name.split(" ")[0] || "");

/** Employee the chatter drawer is scoped to (R11 object identity for the unified feed). */
const chatterObjectId = computed(() => props.employeeId || props.employeeData?.id || null);

const lastName = computed(() => {
  const nameParts = props.name.split(" ").filter(Boolean);
  return nameParts.length > 1 ? nameParts[nameParts.length - 1] : "";
});

function seedEmployeeStore() {
  const store = proxy?.$store;
  if (!store) return;

  const employeeRecord = {
    id: props.employeeId || props.employeeData?.id || null,
    first_name: props.employeeData?.first_name || firstName.value,
    last_name: props.employeeData?.last_name || lastName.value,
    title: props.employeeData?.title || props.title,
    avatar: props.employeeData?.avatar || props.avatar,
    status: props.employeeData?.status || props.status,
    current_status: props.employeeData?.current_status || props.status,
    ...props.employeeData,
  };

  if (!employeeRecord.id) return;

  store.commit("employeeStore/setCurrentEmployee", employeeRecord);
}

onMounted(seedEmployeeStore);

function handleBreadcrumbClick(breadcrumb) {
  emit("breadcrumb-click", breadcrumb);
}

function handleTabChange(index) {
  emit("onChange", index);
  emit("input", index);
}

function handleEmployeeUpdate(employeeData) {
  emit("employeeUpdated", employeeData);
}

function handleEmployeeStatusChanged(employeeData) {
  emit("employeeStatusChanged", employeeData);
}

function handleEmployeeDeleted() {
  emit("employeeDeleted");
}

async function openChatter() {
  chatterDrawer.value.show = true;
  chatterDrawer.value.items = [];
  chatterDrawer.value.page = 1;
  chatterDrawer.value.total = 0;
  await fetchChatter();
}

function closeChatterDrawer() {
  chatterDrawer.value.show = false;
}

async function fetchChatter() {
  if (chatterDrawer.value.loading) return;

  chatterDrawer.value.loading = true;
  proxy.$loader?.show();

  try {
    const accountID = JSON.parse(localStorage.getItem("currentAccountID"));
    const empId = props.employeeId || props.employeeData?.id || null;

    if (!empId) {
      proxy.$notifications?.error("Employee ID not available.");
      return;
    }

    const response = await employeeStore.fetchEmployeeChatter(
      accountID,
      empId,
      chatterDrawer.value.page
    );
    const data = response?.data;

    if (data?.count) {
      chatterDrawer.value.total = data.count;
    }

    if (data?.results) {
      const chatterItems = data.results.map((el) => {
        const date = new Date(el.recorded_on);
        const millisecondsString = date.getTime().toString();
        const nameParts = el.recorded_by_name.split(" ");
        const newEl = { id: el.id };

        if (nameParts.length === 2) {
          newEl.avatar = {
            picture: "",
            firstName: nameParts[0],
            lastName: nameParts[1],
          };
        } else {
          newEl.avatar = {
            picture: "",
            firstName: el.recorded_by_name,
            lastName: "",
          };
        }

        newEl.IsExIQtiveBot = false;
        newEl.message = el.note;
        newEl.created_on = millisecondsString;
        newEl.IsHistory = el.is_history;
        return newEl;
      });

      chatterDrawer.value.items.push(...chatterItems);
    }
  } catch {
    proxy.$notifications?.error("Failed to fetch chatter data.");
  } finally {
    chatterDrawer.value.loading = false;
    proxy.$loader?.hide();
  }
}

function loadMoreChatter() {
  if (chatterDrawer.value.items.length < chatterDrawer.value.total) {
    chatterDrawer.value.page += 1;
    fetchChatter();
  }
}
</script>

<style scoped>
.employee-fair-pay-card-header {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  width: 100%;
}

.employee-fair-pay-card-header__tab-content {
  width: 100%;
  padding: 8px 0 24px;
}

.employee-fair-pay-card-header__page-header {
  display: flex;
  flex-direction: column;
  gap: 1px;
  width: 100%;
  padding: 8px 0;
}

.employee-fair-pay-card-header__top-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 40px;
  width: 100%;
  min-height: 44px;
  padding: 8px 0px;
  box-sizing: border-box;
}

.employee-fair-pay-card-header__breadcrumbs {
  flex: 1 1 auto;
  min-width: 0;
}

.employee-fair-pay-card-header__assignments-badge {
  flex: 0 0 auto;
  margin-left: auto;
}

.employee-fair-pay-card-header__main-row {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding-right: 16px;
}

.employee-fair-pay-card-header__identity {
  display: flex;
  flex: 1 1 0;
  align-items: center;
  gap: 10px;
  min-width: 0;
  padding-right: 20px;
}

.employee-fair-pay-card-header__avatar {
  width: 56px !important;
  min-width: 56px !important;
  height: 56px !important;
  flex-shrink: 0;
}

.employee-fair-pay-card-header__info {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 4px 0;
  min-width: 0;
}

.employee-fair-pay-card-header__name-row {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  min-width: 0;
}

.employee-fair-pay-card-header__name {
  font-family: Inter, sans-serif;
  font-size: 20px;
  font-weight: 600;
  line-height: 24px;
  color: #101828;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.employee-fair-pay-card-header__title {
  font-family: Inter, sans-serif;
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
  color: #98a2b3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.employee-fair-pay-card-header__badge-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.employee-fair-pay-card-header__not-tracking {
  border: 1px solid #eaecf0;
  background-color: #f9fafb;
  color: #344054;
  font-family: Inter, sans-serif;
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
  padding: 2px 8px;
  border-radius: 1000px;
}

.employee-fair-pay-card-header__readiness {
  flex: 0 0 auto;
  width: 375px;
  padding: 16px;
  border-radius: 12px;
  background: #ffffff;
}

.employee-fair-pay-card-header__actions {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  gap: 0;
}

.employee-fair-pay-card-header__overflow-menu {
  flex-shrink: 0;
}

.employee-fair-pay-card-header__action-button {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  border: none;
  background: transparent;
  cursor: pointer;
}

.employee-fair-pay-card-header__action-button:hover {
  background-color: #f2edff;
  border-radius: 8px;
  box-shadow: 0 4px 4px 0 rgba(0, 0, 0, 0.06);
}

.employee-fair-pay-card-header__chatter-icon {
  width: 24px;
  height: 24px;
}

:deep(.employee-fair-pay-card-header__overflow-menu.employee-horizontal-sidebar) {
  margin: 0 !important;
  top: 0 !important;
}

:deep(.employee-fair-pay-card-header__readiness .ex-employee-readiness-box) {
  margin-right: 0;
  width: 100%;
}

:deep(.employee-fair-pay-card-header__readiness .readiness-display) {
  width: 100%;
}

:deep(.employee-fair-pay-card-header__readiness .new-progress-bar-container) {
  width: 100%;
}

:deep(.employee-fair-pay-card-header .employee-fair-pay-menu) {
  width: 100%;
}
</style>
